import {InjectionKey, type Ref, ref} from 'vue';
import axios from 'axios';
import type {Settings} from "@/types";

// Define an InjectionKey for the settings context
export const settingsKey: InjectionKey<{
    settings: Ref<Settings | null>;
    loading: Ref<boolean>;
    error: Ref<string | null>;
    fetchSettings: () => Promise<void>;
}> = Symbol('settings');

/**
 * Provides settings state, a function to fetch settings from the API, and manages
 * the loading and error states.
 *
 * @returns {Object} - The settings state, loading state, error state, and the fetch function.
 *   - `settings`: The settings data, or null if not loaded.
 *   - `loading`: A boolean indicating if the settings are still being fetched.
 *   - `error`: A string containing an error message if fetching fails, or null.
 *   - `fetchSettings`: The function to fetch the settings data.
 */
export function useSettings(): {
    settings: Ref<Settings | null>;
    loading: Ref<boolean>;
    error: Ref<string | null>;
    fetchSettings: () => Promise<void>;
} {
    const settings = ref<Settings | null>(null);
    const loading = ref<boolean>(true);
    const error = ref<string | null>(null);

    /**
     * Fetch settings from the API and update the state.
     */
    const fetchSettings = async () => {
        try {
            const response = await axios.get('/settings');
            settings.value = response.data;
        } catch (err) {
            error.value = 'Failed to load settings';
        } finally {
            loading.value = false;
        }
    };

    return {settings, loading, error, fetchSettings};
}
